// Document editor — uses @symbo.ls/symbolize-editorjs for Editor.js
// integration, dark theming, and block-to-DOMQL conversion.

import { createEditor, destroyEditor, getEditor } from '@symbo.ls/editorjs/init'
import { editorDarkStyles } from '@symbo.ls/editorjs/style'
import { transformEditorJS } from '@symbo.ls/symbolize-editorjs'
import EditorJSMarkdownConverter from '@vingeray/editorjs-markdown-converter'

// ── Helpers ──

import { _navigate } from './Main.js'

// ── Export helpers ──

const _getBlocks = async (s) => {
  const doc = s.root.editorDoc
  if (!doc) return []
  const editor = getEditor()
  if (editor) {
    const saved = await editor.save()
    return saved.blocks || []
  }
  return (doc.content && doc.content.blocks) || []
}

const _downloadFile = (content, filename, mime) => {
  const blob = new Blob([content], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

const _exportMarkdown = async (s) => {
  const blocks = await _getBlocks(s)
  const md = EditorJSMarkdownConverter.toMarkdown(blocks)
  _downloadFile(
    md,
    `${s.root.editorDoc?.title || 'document'}.md`,
    'text/markdown'
  )
}

const _exportHtml = async (s, ext) => {
  const blocks = await _getBlocks(s)
  const title = s.root.editorDoc?.title || 'Document'
  const body = blocks
    .map((b) => {
      const { type, data } = b
      switch (type) {
        case 'header':
          return `<h${data.level || 2}>${data.text || ''}</h${data.level || 2}>`
        case 'paragraph':
          return `<p>${data.text || ''}</p>`
        case 'list': {
          const tag = data.style === 'ordered' ? 'ol' : 'ul'
          const items = (data.items || [])
            .map(
              (i) => `<li>${typeof i === 'string' ? i : i.content || ''}</li>`
            )
            .join('')
          return `<${tag}>${items}</${tag}>`
        }
        case 'quote':
          return `<blockquote>${data.text || ''}</blockquote>`
        case 'code':
          return `<pre><code>${data.code || ''}</code></pre>`
        case 'delimiter':
          return '<hr>'
        default:
          return data?.text ? `<p>${data.text}</p>` : ''
      }
    })
    .join('\n')
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title></head><body>${body}</body></html>`
  _downloadFile(html, `${title}.${ext || 'html'}`, 'text/html')
}

// ── Components ──

export const EditorMain = {
  extends: 'AppMain',
  padding: '0',
  Header: { extends: 'EditorHeader' },
  Body: {
    padding: '0',
    overflow: 'hidden',
    flow: 'y',
    flex: '1',

    EditorArea: {
      if: (el, s) => s.root.editorMode === 'edit'
    },
    ArticleView: {
      if: (el, s) => s.root.editorMode === 'view'
    }
  }
}

export const EditorHeader = {
  extends: 'PageHeader',

  LeftGroup: {
    flow: 'x',
    align: 'center left',
    gap: 'Z',
    flex: '1',
    minWidth: '0',

    BackBtn: {
      extends: 'IconButton',
      Icon: { icon: 'arrow angle left' },
      onClick: (e, el, s) => {
        if (s.root.editorMode === 'edit') {
          destroyEditor()
          s.root.update({ editorMode: 'view' })
          return
        }
        destroyEditor()
        s.root.update({
          activeSection: 'documents',
          editorDoc: null,
          editorMode: null
        })
        _navigate(el, '/documents')
      }
    },

    TitleInput: {
      tag: 'input',
      type: 'text',
      placeholder: 'Document title...',
      if: (el, s) => s.root.editorMode === 'edit',
      padding: '0',
      margin: '0',
      fontSize: 'A',
      fontWeight: '600',
      color: 'title',
      background: 'transparent',
      borderWidth: '0',
      outline: 'none',
      flex: '1',
      minWidth: '0',
      lineHeight: '1',
      onCreate: (el, s) => {
        setTimeout(() => {
          if (el.node) el.node.value = (s.root.editorDoc && s.root.editorDoc.title) || ''
        }, 60)
      },
      onInput: (e, el, s) => {
        const doc = s.root.editorDoc
        if (!doc) return
        s.root.update({ editorDoc: { ...doc, title: e.target.value } })
      }
    },

    TitleText: {
      tag: 'span',
      if: (el, s) => s.root.editorMode === 'view',
      fontSize: 'A',
      fontWeight: '600',
      color: 'title',
      lineHeight: '1',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) =>
        (s.root.editorDoc && s.root.editorDoc.title) || 'Untitled'
    }
  },

  RightGroup: {
    flow: 'x',
    align: 'center right',
    gap: 'Z1',

    EditBtn: {
      tag: 'button',
      type: 'button',
      text: 'Edit',
      if: (el, s) => s.root.editorMode === 'view',
      padding: 'Z A',
      round: 'Z',
      borderWidth: '1px',
      borderColor: 'line',
      borderStyle: 'solid',
      background: 'overlay',
      cursor: 'pointer',
      fontSize: 'Z2',
      fontWeight: '500',
      color: 'title',
      ':hover': { background: 'overlayStrong' },
      onClick: (e, el, s) => {
        let doc = s.root.editorDoc
        if (!doc || !doc.content?.blocks?.length) {
          const hash = typeof window !== 'undefined' ? window.location.hash.slice(1) : ''
          const docId = new URLSearchParams(hash).get('doc')
          if (docId) {
            const match = (s.root.documents || []).find((d) => String(d.id) === docId)
            if (match) {
              const content = match.content && typeof match.content === 'object' ? match.content : { blocks: [] }
              doc = { ...match, content }
            }
          }
        }
        s.root.update({ editorDoc: doc, editorMode: 'edit' })
        setTimeout(() => {
          const inp = document.querySelector('input[placeholder="Document title..."]')
          if (inp && doc) inp.value = doc.title || ''
        }, 100)
      }
    },

    ExportDropdown: {
      if: (el, s) => s.root.editorMode === 'edit',
      position: 'relative',

      ExportBtn: {
        tag: 'button',
        type: 'button',
        text: 'Export',
        padding: 'Z A',
        round: 'Z',
        borderWidth: '1px',
        borderColor: 'line',
        borderStyle: 'solid',
        background: 'overlay',
        cursor: 'pointer',
        fontSize: 'Z2',
        fontWeight: '500',
        color: 'title',
        ':hover': { background: 'overlayStrong' },
        onClick: (e, el, s) => {
          const menu = el.parent.Menu
          if (menu && menu.node) {
            menu.node.style.display =
              menu.node.style.display === 'flex' ? 'none' : 'flex'
          }
        }
      },

      Menu: {
        flow: 'y',
        position: 'absolute',
        top: '100%',
        right: '0',
        marginTop: 'V',
        padding: 'V',
        round: 'Z',
        background: 'overlayStrong',
        borderWidth: '1px',
        borderColor: 'line',
        borderStyle: 'solid',
        zIndex: '100',
        minWidth: 'E',
        display: 'none',
        boxShadow: '0 4px 16px rgba(0,0,0,.3)',

        MdOption: {
          tag: 'button',
          type: 'button',
          text: 'Markdown (.md)',
          padding: 'Z Z1',
          round: 'Y',
          background: 'transparent',
          borderWidth: '0',
          cursor: 'pointer',
          fontSize: 'Z2',
          color: 'title',
          textAlign: 'left',
          ':hover': { background: 'overlay' },
          onClick: async (e, el, s) => {
            el.parent.node.style.display = 'none'
            await _exportMarkdown(s)
          }
        },
        HtmlOption: {
          tag: 'button',
          type: 'button',
          text: 'HTML (.html)',
          padding: 'Z Z1',
          round: 'Y',
          background: 'transparent',
          borderWidth: '0',
          cursor: 'pointer',
          fontSize: 'Z2',
          color: 'title',
          textAlign: 'left',
          ':hover': { background: 'overlay' },
          onClick: async (e, el, s) => {
            el.parent.node.style.display = 'none'
            await _exportHtml(s, 'html')
          }
        },
        PdfOption: {
          tag: 'button',
          type: 'button',
          text: 'PDF (.pdf)',
          padding: 'Z Z1',
          round: 'Y',
          background: 'transparent',
          borderWidth: '0',
          cursor: 'pointer',
          fontSize: 'Z2',
          color: 'caption',
          textAlign: 'left',
          ':hover': { background: 'overlay' },
          onClick: (e, el) => {
            el.parent.node.style.display = 'none'
            window.print()
          }
        },
        DocxOption: {
          tag: 'button',
          type: 'button',
          text: 'Word (.docx)',
          padding: 'Z Z1',
          round: 'Y',
          background: 'transparent',
          borderWidth: '0',
          cursor: 'pointer',
          fontSize: 'Z2',
          color: 'caption',
          textAlign: 'left',
          ':hover': { background: 'overlay' },
          onClick: async (e, el, s) => {
            el.parent.node.style.display = 'none'
            await _exportHtml(s, 'doc')
          }
        }
      }
    },

    SaveBtn: {
      tag: 'button',
      type: 'button',
      text: 'Save',
      if: (el, s) => s.root.editorMode === 'edit',
      padding: 'Z A',
      round: 'Z',
      borderWidth: '1px',
      borderColor: 'line',
      borderStyle: 'solid',
      theme: 'primary',
      cursor: 'pointer',
      fontSize: 'Z2',
      fontWeight: '600',
      ':hover': { opacity: '.9' },
      onClick: async (e, el, s) => {
        const doc = s.root.editorDoc
        if (!doc) return
        let content = doc.content
        try {
          const editor = getEditor()
          if (editor) content = await editor.save()
        } catch (err) {
          console.error('[Save] Editor.js save failed:', err)
        }
        // Don't save empty drafts — require title change or content
        const hasContent = content?.blocks?.length > 0
        const hasTitle = doc.title && doc.title !== 'Untitled document'
        if (doc.isDraft && !hasContent && !hasTitle) return
        const today = new Date().toISOString().slice(0, 10)
        // migrated 2026-04-27 — upsert via SDK route. doc.source === 'kb'
        // routes through documents.kb (knowledge base articles); otherwise
        // documents.notes (personal/team notes). isUUID heuristic preserved
        // so existing saved docs update by id; new docs let the server mint.
        const isUUID = typeof doc.id === 'string' && doc.id.includes('-')
        const row = {
          title: doc.title,
          content,
          updated_at: new Date().toISOString(),
          ...(isUUID ? { id: doc.id } : {})
        }
        const route = doc.source === 'kb'
          ? 'workspaceProject.documents.kb'
          : 'workspaceProject.documents.notes'
        let savedId = doc.id
        try {
          // create handles upsert server-side when row.id is set
          const data = await (el.sdk || (await import('../../../shared/functions/sdk.js')).getSDK())
            .execute(route, 'create', { payload: row })
          if (data?.id) savedId = data.id
        } catch (err) {
          console.warn('[Save] SDK upsert failed, local-only:', err?.message || err)
        }
        destroyEditor()
        if (doc.source === 'kb') {
          const kbArticles = (s.root.kbArticles || []).map((a) =>
            a.id === savedId
              ? { ...a, title: doc.title, content, updatedAt: today }
              : a
          )
          s.root.update({
            kbArticles,
            editorDoc: { ...doc, id: savedId, content },
            editorMode: 'view'
          })
        } else {
          const documents = (s.root.documents || []).map((d) =>
            d.id === savedId
              ? { ...d, title: doc.title, content, updatedAt: today }
              : d
          )
          s.root.update({
            documents,
            editorDoc: { ...doc, id: savedId, content },
            editorMode: 'view'
          })
        }
      }
    }
  }
}

// ── Editor area (edit mode) ──

export const EditorArea = {
  flex: '1',
  overflow: 'auto',
  padding: 'A2',
  position: 'relative',
  color: 'title',
  borderColor: 'line',
  ...editorDarkStyles,

  EditorHolder: {
    attr: { id: 'editorjs' },
    maxWidth: 'J',
    width: '100%',
    margin: '0 auto',
    minHeight: 'G',
    flex: '1',
    position: 'relative',
    padding: 'Y',

    onCreate: (el, s) => {
      let doc = s.root.editorDoc
      if (!doc || !doc.content || !doc.content.blocks?.length) {
        const hash = typeof window !== 'undefined' ? window.location.hash.slice(1) : ''
        const docId = new URLSearchParams(hash).get('doc')
        if (docId) {
          const match = (s.root.documents || []).find((d) => String(d.id) === docId)
          if (match) {
            const content = match.content && typeof match.content === 'object' ? match.content : { blocks: [] }
            doc = { ...match, content }
            s.root.update({ editorDoc: doc })
          }
        }
      }
      const data =
        doc && doc.content && typeof doc.content === 'object'
          ? doc.content
          : { blocks: [] }
      setTimeout(() => {
        createEditor(el.node, data, {
          onChange: (saved) => {
            const current = s.root.editorDoc
            if (current)
              s.root.update({ editorDoc: { ...current, content: saved } })
          }
        })
      }, 50)
    },

    onDestroy: () => {
      destroyEditor()
    }
  }
}

// ── Rendered article view (after save) ──
// Uses transformEditorJS from symbolize-editorjs to convert blocks to DOMQL

export const ArticleView = {
  flex: '1',
  overflow: 'auto',
  padding: 'A',
  color: 'title',

  Article: {
    maxWidth: 'J',
    width: '100%',
    margin: '0 auto',
    flow: 'y',
    gap: 'A',

    Title: {
      tag: 'h1',
      fontSize: 'D',
      fontWeight: '700',
      color: 'title',
      text: (el, s) =>
        (s.root.editorDoc && s.root.editorDoc.title) || 'Untitled'
    },

    P: {
      flow: 'x',
      align: 'center left',
      gap: 'Z1',
      fontSize: 'Y2',
      color: 'caption',
      marginBottom: 'A',

      AuthorAvatar: {
        extends: 'Avatar',
        avatarType: 'initials',
        boxSize: 'B B',
        src: (el, s) => {
          const email = s.root.currentUserEmail
          const person = el.call('findPerson', email)
          return el.call('avatarUrl', (person && person.name) || email)
        }
      },
      AuthorName: {
        tag: 'span',
        fontWeight: '500',
        color: 'subtitle',
        text: (el, s) => {
          const email = s.root.currentUserEmail
          const person = el.call('findPerson', email)
          return (person && person.name) || email || ''
        }
      },
      Sep: { tag: 'span', text: '·' },
      Date: {
        tag: 'span',
        text: (el, s) => {
          const doc = s.root.editorDoc
          if (!doc) return ''
          const d = (s.root.documents || []).find((x) => x.id === doc.id)
          return d?.updatedAt ? `Updated ${d.updatedAt}` : ''
        }
      }
    },

    Divider: { height: '1px', background: 'line', margin: '0 0 Z 0' },

    ContentBlocks: {
      flow: 'y',
      gap: 'Z1',

      onRender: (el, s) => {
        const doc = s.root.editorDoc
        if (!doc?.content) return
        const domqlObj = transformEditorJS(doc.content)
        // Render the DOMQL component tree
        if (el.node) el.node.innerHTML = ''
        const keys = Object.keys(domqlObj)
        if (keys.length) {
          const update = {}
          keys.forEach((key) => {
            update[key] = domqlObj[key]
          })
          el.set(update)
        }
      }
    }
  }
}
